<template>
  <div class="carrossel-listagem">
    <Panel lineColor="var(--primary-color)">
      <template v-slot:options>
        <FormButtonGroup align="end" spaced>
          <FormButton
            :text="labels.botaoNovo"
            type="primario"
            @click="onNovo"
          />
        </FormButtonGroup>
      </template>

      <div class="data-table-filter">
        <v-row>
          <v-col cols="12" md="4" lg="3" xl="2" class="mb-n5">
            <FormField
              v-model="filters.titulo"
              :label="labels.placeholderBusca"
              dense
              clearable
            />
          </v-col>
          <v-col cols="12" md="4" lg="3" xl="2" class="mb-n5">
            <FormAutocomplete
              v-model="filters.ativo"
              label="Status"
              dense
              clearable
              :options="statusOptions"
            />
          </v-col>
          <v-col cols="12" md="4" lg="3" xl="2" class="mb-n5">
            <FormAutocomplete
              v-model="filters.local"
              label="Local de exibição"
              dense
              clearable
              :options="localOptions"
            />
          </v-col>
          <v-col cols="12" md="4" lg="3" xl="2" class="mb-n5">
            <FormDatePicker
              v-model="filters.periodoInicio"
              label="Período início"
              dense
              clearable
            />
          </v-col>
          <v-col cols="12" md="4" lg="3" xl="2" class="mb-n5">
            <FormDatePicker
              v-model="filters.periodoFim"
              label="Período fim"
              dense
              clearable
            />
          </v-col>
          <v-col
            cols="12"
            md="4"
            lg="3"
            xl="2"
            class="d-flex align-center mt-md-n2"
          >
            <FormButton
              text="Limpar filtros"
              outlined
              color="primario"
              full
              before-icon="fas fa-sliders-h"
              @click="onLimparFiltros"
            />
          </v-col>
        </v-row>
      </div>

      <v-divider class="mb-5" />

      <div class="listagem-table-wrapper">
        <v-data-table
          :headers="headers"
          :items="rows"
          :loading="loading"
          :options="tableOptions"
          :mobile-breakpoint="0"
          item-key="idcarrossel"
          class="listagem-table"
          :class="{ 'listagem-table--dark': darkEnabled }"
          hide-default-footer
        >
          <template v-slot:item.titulo="{ item }">
            <div class="listagem-carrossel-cell">
              <div class="listagem-carrossel-cell__name">{{ item.titulo }}</div>
              <div
                v-if="isStaticImages"
                class="listagem-carrossel-cell__previews"
              >
                <img
                  v-for="(url, index) in getPreviewImages(item)"
                  :key="`${item.idcarrossel}-${index}`"
                  :src="url"
                  class="listagem-carrossel-cell__thumb"
                  alt=""
                />
              </div>
            </div>
          </template>

          <template v-slot:item.local="{ item }">
            <span class="listagem-local">
              <v-icon small class="mr-3">fas fa-desktop</v-icon>
              {{ formatLocalExibicao(item.localexibicao, setoresFolha) }}
            </span>
          </template>

          <template v-slot:item.vigencia="{ item }">
            {{ formatVigencia(item.inicio_vigencia, item.fim_vigencia) }}
          </template>

          <template v-slot:item.banners="{ item }">
            {{ formatBannersLabel(getBannersCount(item)) }}
          </template>

          <template v-slot:item.categoriaTipo="{ item }">
            {{ getVitrineCategoriaTipoLabel(item, carouselTypeVariant) }}
          </template>

          <template v-slot:item.status="{ item }">
            <Chip
              class="listagem-status-chip"
              :text="item.ativo ? 'Ativo' : 'Inativo'"
              :color="item.ativo ? 'success' : 'grey'"
              :small="true"
              pill
              icon-dot
              icon="fas fa-circle"
            />
          </template>

          <template v-slot:item.acoes="{ item }">
            <div class="listagem-actions">
              <v-btn icon title="Editar" @click="onEditar(item)">
                <v-icon small color="primario">fas fa-pencil-alt</v-icon>
              </v-btn>
              <v-btn icon title="Duplicar" @click="onDuplicar(item)">
                <v-icon small color="primario">fas fa-copy</v-icon>
              </v-btn>
              <FormButtonMenu
                icon
                type="primario"
                :options="getMenuOptions(item)"
                :loading="Boolean(loadingMenuRow[item.idcarrossel])"
                @click="option => onMenuAction(item, option)"
              >
                <v-icon small color="primario">fas fa-ellipsis-v</v-icon>
              </FormButtonMenu>
            </div>
          </template>

          <template v-slot:no-data>
            <div class="py-6 text-center grey--text">
              Nenhum {{ labels.itemSingular }} encontrado.
            </div>
          </template>

          <template
            v-slot:footer="{
              props: { pagination: { pageStart, pageStop } }
            }"
          >
            <div
              class="custom-footer"
              :class="{ 'custom-footer--dark': darkEnabled }"
            >
              <div class="perpage-selector">
                <span>Itens por página:</span>
                <v-menu offset-y v-model="selectorPageOpen">
                  <template v-slot:activator="{ on }">
                    <v-btn text v-on="on" :class="{ open: selectorPageOpen }">
                      <span style="margin-right:10px;">{{
                        options.itemsPerPage
                      }}</span>
                      <v-icon class="icon-selector">fas fa-caret-down</v-icon>
                    </v-btn>
                  </template>
                  <v-list>
                    <v-list-item
                      v-for="n in limitPage"
                      :key="n"
                      @click="selectPerPage(n)"
                    >
                      {{ n }}
                    </v-list-item>
                  </v-list>
                </v-menu>
              </div>
              <div class="paga-index">
                Exibindo {{ calculePageStart(pageStart) }} a
                {{ pageStop + (options.start - 1 || 0) }} de
                {{ options.itemsLength }} registros
              </div>
              <div class="pagination-controll">
                <v-btn text :disabled="options.page <= 0" @click="prevPage">
                  <v-icon size="24">fas fa-chevron-left</v-icon>
                </v-btn>
                <v-btn
                  text
                  :disabled="
                    options.itemsLength <= 0 ||
                      pageStop + options.start - 1 >= options.itemsLength
                  "
                  @click="
                    nextPage(options.itemsLength, pageStop + options.start - 1)
                  "
                >
                  <v-icon size="24">fas fa-chevron-right</v-icon>
                </v-btn>
              </div>
            </div>
          </template>
        </v-data-table>
      </div>
    </Panel>
  </div>
</template>

<script>
import * as moment from "moment";
import * as _ from "lodash";
import Panel from "@/components/Panel";
import FormButton from "@/components/form/FormButton";
import FormButtonGroup from "@/components/form/FormButtonGroup";
import FormButtonMenu from "@/components/form/FormButtonMenu";
import FormField from "@/components/form/FormField";
import FormAutocomplete from "@/components/form/FormAutocomplete";
import FormDatePicker from "@/components/form/FormDatePicker";
import Chip from "@/components/Chip";
import { mapGetters } from "vuex";
import { SEGMENTO_LIST } from "@/store/actions/segmento";
import { TODOS_SETORES } from "@/store/actions/setor";
import {
  CARROSSEL_LIST,
  CARROSSEL_GET,
  CARROSSEL_UPDATE,
  CARROSSEL_DELETE,
  CARROSSEL_DUPLICATE
} from "@/store/actions/carrossel";
import {
  formatBannersLabel,
  formatLocalExibicao,
  formatVigencia,
  getBannersCount,
  getListagemColumns,
  getListagemLabels,
  getLocalExibicaoOptions,
  getPreviewImages,
  getStatusFilterOptions,
  getVitrineCategoriaTipoLabel
} from "@/pages/customizacao/utils/utilCarrossel";

const defaultFilters = () => ({
  titulo: "",
  ativo: null,
  local: "",
  periodoInicio: null,
  periodoFim: null
});

export default {
  name: "CarrosselListagem",
  components: {
    Panel,
    FormButton,
    FormButtonGroup,
    FormButtonMenu,
    FormField,
    FormAutocomplete,
    FormDatePicker,
    Chip
  },
  props: {
    tipoConteudo: {
      type: String,
      required: true
    },
    baseRoute: {
      type: String,
      required: true
    },
    carouselTypeVariant: {
      type: String,
      default: "type1"
    }
  },
  data() {
    return {
      rows: [],
      total: 0,
      loading: false,
      setoresFolha: [],
      filters: defaultFilters(),
      suppressFiltersWatch: false,
      options: {
        itemsPerPage: 10,
        page: 0,
        start: 1,
        itemsLength: 0
      },
      selectorPageOpen: false,
      limitPage: [5, 10, 15, 20, 30],
      loadingMenuRow: {}
    };
  },
  computed: {
    ...mapGetters(["getFornecedorId", "darkEnabled", "moduloFornecedorAtivo"]),
    labels() {
      return getListagemLabels(this.tipoConteudo);
    },
    isStaticImages() {
      return this.tipoConteudo === "STATIC_IMAGES";
    },
    headers() {
      return getListagemColumns(this.tipoConteudo);
    },
    statusOptions() {
      return getStatusFilterOptions();
    },
    localOptions() {
      return getLocalExibicaoOptions(this.setoresFolha);
    },
    tableOptions() {
      return {
        ...this.options,
        page: 0
      };
    }
  },
  watch: {
    tipoConteudo: {
      immediate: true,
      handler() {
        this.debouncedLoadFromFilters?.cancel();
        this.suppressFiltersWatch = true;
        this.filters = defaultFilters();
        this.options = { ...this.options, page: 0, start: 1 };
        this.loadSetores().then(() => {
          this.loadData();
          this.$nextTick(() => {
            this.suppressFiltersWatch = false;
          });
        });
      }
    },
    filters: {
      deep: true,
      handler() {
        if (this.suppressFiltersWatch) return;
        this.debouncedLoadFromFilters();
      }
    }
  },
  created() {
    this.debouncedLoadFromFilters = _.debounce(() => {
      this.options = { ...this.options, page: 0, start: 1 };
      this.loadData();
    }, 500);
  },
  beforeDestroy() {
    this.debouncedLoadFromFilters?.cancel();
  },
  methods: {
    formatLocalExibicao,
    formatVigencia,
    formatBannersLabel,
    getBannersCount,
    getPreviewImages,
    getVitrineCategoriaTipoLabel,
    async loadSetores() {
      try {
        const segmentos = await this.$store.dispatch(SEGMENTO_LIST);
        const idSegmentos = segmentos.map(s => s.idsegmento);
        const folhas = [];

        for (const segmento of segmentos) {
          const setores = await this.$store.dispatch(
            TODOS_SETORES,
            segmento.idsegmento
          );
          folhas.push(
            ...setores.filter(f => idSegmentos.indexOf(f.idsegmento) >= 0)
          );
        }

        this.setoresFolha = folhas;
      } catch (error) {
        this.setoresFolha = [];
      }
    },
    buildApiFilters() {
      const filters = {
        tipoConteudo: this.tipoConteudo,
        limit: this.options.itemsPerPage,
        offset: this.options.page * this.options.itemsPerPage
      };

      const titulo = `${this.filters.titulo || ""}`.trim();
      if (titulo) {
        filters.titulo = titulo;
      }
      if (typeof this.filters.ativo === "boolean") {
        filters.ativo = this.filters.ativo;
      }
      if (this.filters.local) {
        filters.local = this.filters.local;
      }
      if (this.filters.periodoInicio) {
        filters.periodoInicio = moment(this.filters.periodoInicio)
          .startOf("day")
          .toISOString();
      }
      if (this.filters.periodoFim) {
        filters.periodoFim = moment(this.filters.periodoFim)
          .endOf("day")
          .toISOString();
      }

      return filters;
    },
    async loadData() {
      this.loading = true;
      try {
        const result = await this.$store.dispatch(
          CARROSSEL_LIST,
          this.buildApiFilters()
        );
        this.rows = result.data || [];
        this.total = result.total || 0;
        this.options.itemsLength = this.total;
      } catch (error) {
        this.rows = [];
        this.total = 0;
        this.options.itemsLength = 0;
      } finally {
        this.loading = false;
      }
    },
    onLimparFiltros() {
      this.debouncedLoadFromFilters.cancel();
      this.suppressFiltersWatch = true;
      this.filters = defaultFilters();
      this.options = { ...this.options, page: 0, start: 1 };
      this.loadData();
      this.$nextTick(() => {
        this.suppressFiltersWatch = false;
      });
    },
    selectPerPage(itemsPerPage) {
      this.options = {
        ...this.options,
        itemsPerPage,
        page: 0,
        start: 1
      };
      this.loadData();
    },
    prevPage() {
      if (this.options.page > 0) {
        const newPage = this.options.page - 1;
        this.options = {
          ...this.options,
          page: newPage,
          start: newPage * this.options.itemsPerPage + 1
        };
        this.loadData();
      }
    },
    nextPage(itemsLength, pageStop) {
      if (itemsLength > 0 && pageStop < itemsLength) {
        const newPage = this.options.page + 1;
        this.options = {
          ...this.options,
          page: newPage,
          start: newPage * this.options.itemsPerPage + 1
        };
        this.loadData();
      }
    },
    calculePageStart(pageStart) {
      if (this.options.itemsLength > 0) {
        return this.options.start + pageStart;
      }
      return pageStart;
    },
    onNovo() {
      this.$router.push(`${this.baseRoute}/criar`);
    },
    onEditar(item) {
      this.$router.push(`${this.baseRoute}/editar/${item.idcarrossel}`);
    },
    getMenuOptions(item) {
      const options = [];

      if (item.ativo) {
        options.push({
          text: this.labels.menuDesativar,
          value: "deactivate"
        });
      } else {
        options.push({
          text: this.labels.menuAtivar,
          value: "activate"
        });
      }

      options.push({
        text: this.labels.menuExcluir,
        value: "delete"
      });

      return options;
    },
    onMenuAction(item, option) {
      if (option.value === "activate") {
        this.onAlterarStatus(item, true);
        return;
      }

      if (option.value === "deactivate") {
        this.onAlterarStatus(item, false);
        return;
      }

      if (option.value === "delete") {
        this.onExcluir(item);
      }
    },
    onAlterarStatus(item, ativo) {
      const isAtivacao = ativo === true;

      this.$swal
        .fire({
          title: isAtivacao
            ? this.labels.confirmarAtivacaoTitulo
            : this.labels.confirmarDesativacaoTitulo,
          text: isAtivacao
            ? this.labels.confirmarAtivacaoTexto
            : this.labels.confirmarDesativacaoTexto,
          type: "question",
          showCancelButton: true,
          confirmButtonText: "Confirmar",
          cancelButtonText: "Cancelar",
          reverseButtons: true
        })
        .then(result => {
          if (!result.value) return;
          this.alterarStatus(item, ativo);
        });
    },
    async alterarStatus(item, ativo) {
      const idcarrossel = item.idcarrossel;
      this.$set(this.loadingMenuRow, idcarrossel, true);

      try {
        const carrossel = await this.$store.dispatch(
          CARROSSEL_GET,
          idcarrossel
        );

        if (ativo && this.isStaticImages && this.labels.ativacaoSemBannerErro) {
          const slides = (carrossel.slides || []).filter(slide => slide?.image);
          if (!slides.length) {
            this.$vueOnToast.pop("error", this.labels.ativacaoSemBannerErro);
            return;
          }
        }

        await this.$store.dispatch(CARROSSEL_UPDATE, {
          idcarrossel,
          configs: {
            ...carrossel,
            ativo
          }
        });

        this.$vueOnToast.pop(
          "success",
          ativo ? this.labels.ativacaoSucesso : this.labels.desativacaoSucesso
        );
        this.loadData();
      } catch (error) {
        this.$vueOnToast.pop(
          "error",
          ativo ? this.labels.ativacaoErro : this.labels.desativacaoErro
        );
      } finally {
        this.$set(this.loadingMenuRow, idcarrossel, false);
      }
    },
    onDuplicar(item) {
      this.$swal
        .fire({
          title: this.labels.confirmarDuplicacaoTitulo,
          text: this.labels.confirmarDuplicacaoTexto,
          type: "question",
          showCancelButton: true,
          confirmButtonText: "Confirmar",
          cancelButtonText: "Cancelar",
          reverseButtons: true
        })
        .then(result => {
          if (!result.value) return;

          this.loading = true;
          this.$store
            .dispatch(CARROSSEL_DUPLICATE, item.idcarrossel)
            .then(() => {
              this.$vueOnToast.pop("success", this.labels.duplicacaoSucesso);
              this.loadData();
            })
            .catch(() => {
              this.loading = false;
              this.$vueOnToast.pop("error", this.labels.duplicacaoErro);
            });
        });
    },
    onExcluir(item) {
      if (
        this.isStaticImages &&
        item.ativo &&
        this.labels.bloqueioExclusaoAtivoTitulo
      ) {
        this.$swal.fire({
          title: this.labels.bloqueioExclusaoAtivoTitulo,
          text: this.labels.bloqueioExclusaoAtivoTexto,
          type: "warning",
          confirmButtonText: "Ok",
          reverseButtons: true
        });
        return;
      }

      this.$swal
        .fire({
          title: this.labels.confirmarExclusaoTitulo,
          text: "Esta operação é irreversível. Continuar?",
          type: "warning",
          showCancelButton: true,
          confirmButtonText: "Confirmar",
          cancelButtonText: "Cancelar",
          reverseButtons: true
        })
        .then(result => {
          if (result.value) {
            this.$store
              .dispatch(CARROSSEL_DELETE, item.idcarrossel)
              .then(() => {
                this.$vueOnToast.pop(
                  "success",
                  `${this.labels.itemSingular} excluído com sucesso.`
                );
                this.loadData();
              })
              .catch(() => {
                this.$vueOnToast.pop(
                  "error",
                  `Erro ao excluir ${this.labels.itemSingular}.`
                );
              });
          }
        });
    }
  }
};
</script>

<style scoped lang="scss">
.listagem-header {
  padding: 8px 16px 0;

  &__title {
    font-size: 1.25rem;
    font-weight: 600;
    margin: 0 0 4px;
    color: #333;
  }

  &__subtitle {
    margin: 0 0 12px;
    color: #666;
    font-size: 0.9rem;
  }
}

.data-table-filter {
  padding: 0 16px 15px;
}

.listagem-table-wrapper {
  overflow-x: auto;
  -webkit-overflow-scrolling: touch;
  padding: 0 8px;
}

.listagem-table {
  min-width: 720px;
}

.listagem-carrossel-cell {
  display: flex;
  flex-direction: column;
  gap: 8px;
  padding: 8px 0;

  &__name {
    font-weight: 600;
    color: #333;
  }

  &__previews {
    display: flex;
    gap: 6px;
    flex-wrap: wrap;
  }

  &__thumb {
    width: 48px;
    height: 32px;
    object-fit: cover;
    border-radius: 4px;
    border: 1px solid #e0e0e0;
  }
}

.listagem-local {
  display: inline-flex;
  align-items: center;
  font-size: 0.875rem;
}

.listagem-actions {
  display: flex;
  justify-content: center;
  gap: 4px;
}

.listagem-status-chip {
  margin: 0 !important;
}

.custom-footer {
  display: flex;
  flex-wrap: wrap;
  justify-content: flex-end;
  align-items: center;
  font-size: 12px;
  border-top: 1px solid rgba(0, 0, 0, 0.12);
  padding: 10px 20px;

  .paga-index {
    padding: 0 15px;
  }

  .perpage-selector {
    min-width: 50px;
    display: flex;
    align-items: center;
    flex: 0 0 0;
    justify-content: flex-end;
    white-space: nowrap;

    .icon-selector {
      transition: all 0.3s ease-in-out;
    }

    .open {
      .icon-selector {
        transform: rotate(180deg);
      }
    }
  }

  .pagination-controll {
    width: 100px;
    display: flex;
    justify-content: center;
    align-items: center;

    button {
      width: 50px;
      padding: 5px;
      min-width: initial;
      height: initial;
    }

    button:last-child {
      margin-left: 5px;
    }
  }
}

.custom-footer--dark {
  border-top-color: rgba(255, 255, 255, 0.12);
  color: rgba(255, 255, 255, 0.7);
}

@media (max-width: 768px) {
  .custom-footer {
    flex-direction: column;

    .paga-index,
    .pagination-controll {
      padding: 7px 15px;
    }
  }
}

.listagem-table--dark .listagem-carrossel-cell__name {
  color: rgba(255, 255, 255, 0.87);
}
</style>
